<template>
  <!-- Esc and a click beside it keep editing: the one answer that never loses anything. Also while a
       save is on its way, so a request that hangs cannot hold the app behind this dialog. -->
  <v-dialog :model-value="Boolean(prompt)" max-width="480" @update:model-value="open => !open && choose('stay')">
    <v-card v-if="prompt" class="pb-2">
      <v-card-title class="text-h5">{{ $t("general.unsaved.dialog.title") }}</v-card-title>

      <v-card-text>
        <p class="mb-3">{{ $t("general.unsaved.dialog.text") }}</p>
        <!-- Names are not passed to $t: vue-i18n escapes parameters, and "C25/30" would come out as
             "C25&#x2F;30". -->
        <div v-for="(entry, index) in prompt.entries" :key="index" class="unsaved-dialog__entry">
          <v-icon size="18" :icon="entry.label().icon" class="unsaved-dialog__icon" />
          <span class="text-truncate">{{ entry.label().name }}</span>
        </div>
        <p v-if="!prompt.canSave" class="text-medium-emphasis mt-3 mb-0">
          {{ $t("general.unsaved.dialog.save_there") }}
        </p>
      </v-card-text>

      <v-card-actions class="flex-wrap">
        <v-btn class="font-weight-bold" @click="choose('stay')">
          {{ $t("general.unsaved.dialog.stay") }}
        </v-btn>
        <v-spacer />
        <v-btn color="error" class="font-weight-bold" :disabled="prompt.saving" @click="choose('discard')">
          {{ $t("general.unsaved.dialog.discard") }}
        </v-btn>
        <v-btn
          v-if="prompt.canSave"
          color="primary"
          variant="flat"
          class="font-weight-bold"
          :loading="prompt.saving"
          @click="choose('save')"
        >
          {{ $t("general.unsaved.dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/**
 * The question before edits not saved yet are left behind: by a navigation, signing out, closing a
 * report panel. The state lives in `services/unsavedChanges.ts`; this is its only renderer, mounted
 * once in App.vue so it outlives the page it asks about.
 */
import { leavePrompt as prompt, type LeaveChoice } from "@/services/unsavedChanges"

function choose(choice: LeaveChoice) {
  if (!prompt.value || (prompt.value.saving && choice !== "stay")) return
  prompt.value.resolve(choice)
}
</script>

<style scoped>
.unsaved-dialog__entry {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 4px 0;
  font-weight: 600;
}

.unsaved-dialog__icon {
  flex: none;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
